/*
 * Copyright (C) 2018 Frontier Developments PLC.
 */

import {ActionTree} from 'vuex';
import {RootState} from '@/store/types';
import {UgcItemInterface, UgcItemStateInterface} from '@/store/modules/ugc-item/types';
import api from '@/services/api';
import {AxiosResponse} from 'axios';
import {UserSessionInterface} from '@/store/modules/user-session/types';
import {UgcNotificationItemInterface} from '@/store/modules/ugc-notification/types';

/**
 * Actions file for UGC item operations.
 *
 * Actions are functions that can be exported and called upon potentially in an asynchronous manner.
 * To add an action export a named const function. e.g. export const fnName = ({ commit }, {id, name}) => { ... }
 *
 * @author Jonny Cook <jcook@frontier.co.uk>
 */
export const actions: ActionTree<UgcItemStateInterface, RootState> = {
    /**
     * Load UGC items action.
     *
     * @param {any} commit
     * @param {UserSessionInterface} options
     */
    async loadItems({commit}: {commit: any}): Promise<UgcItemInterface[]> {
        return api.axios.get('api/item').then((response: AxiosResponse<UgcItemInterface[]>) => {
            const items: UgcItemInterface[] = response.data.map((item: UgcItemInterface) => {
                item.price /= 100;
                item.runningCost /= 100;
                return item;
            });
            commit('setItems', items);
            return items;
        });
    },

    /**
     * Apply notifications to current data set.
     *
     * @param {any} commit
     * @param {UgcNotificationItemInterface[]} notifications
     */
    notifications({commit}: {commit: any}, notifications: UgcNotificationItemInterface[]): void {
        commit('applyNotifications', notifications);
    },


    /**
     * Delete a UGC item.
     *
     * @param {any} commit
     * @param {UgcItemInterface} item
     *
     * @returns {Promise<void>}
     */
    deleteItem({commit}: {commit: any}, item: UgcItemInterface): Promise<void> {
        return api.axios.delete(`api/item/${item.id}`)
            .then((response: AxiosResponse<void>) => {
                commit('deleteItem', item);
                return response.data;
            });
    },

    /**
     * Remove a UGC item by id.
     *
     * @param {any} commit
     * @param {string} itemId
     */
    removeItem({commit}: {commit: any}, itemId: string): void {
        commit('deleteItem', {id: itemId});
    },

};
